/**
 * Trustpilot Transformers — wire-format → flat lowerCamelCase records
 * (Story 23.3 AC #4, #12)
 *
 * Trustpilot's wire format is ALREADY mostly camelCase (unlike CallRail's
 * snake_case), but the transformers flatten nested object access
 * (`response.score.trustScore` → `record.trustScore`) and de-nest the
 * `companyReply` object into per-row fields (`companyReplyText`,
 * `companyReplyCreatedAt`, `hasReply`, `replyLatencySeconds`).
 *
 * **CSV-injection sanitization** is applied to every string-valued field
 * destined for export via `stripCsvInjectionPrefix` (re-exported from
 * CallRail's `pii.ts`).
 *
 * The build-time test in `transformers.test.ts` asserts no Trustpilot
 * record key ever uses nested-object shape (e.g., `record.score` is
 * undefined, `record.trustScore` is defined).
 */
import { stripCsvInjectionPrefix } from "./pii";

/**
 * `_reportType` discriminator literals — every record carries exactly
 * one. Mirrors Google Reviews 23.1 + CallRail 22.1 `_reportType` shape.
 *
 * Six discriminators in the production-or-fixture universe:
 *   - `businessUnitDetails` — Story 23.3 production marquee BU row (1/fetch)
 *   - `reviewSummary` — Story 23.3 production aggregations row (1/fetch)
 *   - `reviewDistribution` — Story 23.3 production star-distribution rows (5/fetch)
 *   - `trustpilotReview` — Story 23.3 production per-review rows (N/fetch)
 *   - `contentIntegrityFlag` — Story 23.4 fixture-only rows; production
 *     surface deferred (Round 4 ADR-D)
 *   - `invitationData` — Story 35.3 production-only row (0 or 1/fetch,
 *     emitted when `privateApiEntitled === true` AND dateRange overlaps
 *     with the 30-day Invitations retention window)
 *
 * Code-review round 1 fix #37 — added `contentIntegrityFlag` to the
 * union so the "6 discriminators" claim is type-honest.
 */
export type TrustpilotReportType =
  | "businessUnitDetails"
  | "reviewSummary"
  | "reviewDistribution"
  | "trustpilotReview"
  | "contentIntegrityFlag"
  | "invitationData";

/**
 * Compute a sentiment bucket from a star rating per AC #4:
 *  - 4-5 stars → `"positive"`
 *  - 3 stars   → `"neutral"`
 *  - 1-2 stars → `"negative"`
 */
export function trustpilotSentimentBucket(
  stars: number
): "positive" | "neutral" | "negative" {
  if (stars >= 4) return "positive";
  if (stars === 3) return "neutral";
  return "negative";
}

/**
 * Transform a Trustpilot Business Unit Details API response into the flat
 * marquee KPI row (`_reportType: "businessUnitDetails"`).
 *
 * Story 35.3 AC #5 — widens the projection with:
 *   - `email` + `phone` (PII-flagged via `sanitizeTrustpilotPii`)
 *   - `addressStreet` + `addressPostcode` (CSV-injection stripped)
 *   - `description` (object pass-through)
 *   - `socialmedia` (object pass-through)
 *   - `badFit` (Boolean — Airbyte manifest field)
 */
export function transformTrustpilotBusinessUnitDetails(
  raw: Record<string, unknown>,
  businessUnitId: string
): Record<string, unknown> {
  const score = (raw.score ?? {}) as Record<string, unknown>;
  const numberOfReviews = (raw.numberOfReviews ?? {}) as Record<string, unknown>;
  const address = (raw.address ?? {}) as Record<string, unknown>;
  return {
    _reportType: "businessUnitDetails" as TrustpilotReportType,
    businessUnitId,
    displayName: safeString(raw.displayName),
    companyName: safeString(raw.companyName),
    // Story 35.3 — PII-flagged. The sanitizer redacts in log paths, but
    // we still surface in the record so widgets can render them when
    // the agency operator views the report.
    email: stringOrNull(raw.email),
    phone: stringOrNull(raw.phone),
    country: typeof address.countryCode === "string" ? address.countryCode : null,
    addressCity: safeString(address.city),
    // Story 35.3 — address sub-field expansion per AC #5. CSV-injection
    // strip applied via Task 10 (`stripCsvInjectionPrefix`) — these
    // strings are business addresses, agency-controlled, but defense-
    // in-depth against pathological inputs.
    addressStreet: applyCsvStrip(safeString(address.street)),
    addressPostcode: applyCsvStrip(safeString(address.postcode)),
    description:
      typeof raw.description === "object" && raw.description !== null
        ? (raw.description as Record<string, unknown>)
        : null,
    socialmedia:
      typeof raw.socialmedia === "object" && raw.socialmedia !== null
        ? (raw.socialmedia as Record<string, unknown>)
        : null,
    badFit: typeof raw.badFit === "boolean" ? raw.badFit : null,
    trustScore: numberOrNull(score.trustScore),
    stars: integerOrNull(score.stars),
    totalReviews: integerOrNull(numberOfReviews.total),
    reviewsUsedForTrustScoreCalculation: integerOrNull(
      numberOfReviews.usedForTrustScoreCalculation
    ),
    numberOfReviewsTotal: integerOrNull(numberOfReviews.total),
    isSubscriber: typeof raw.isSubscriber === "boolean" ? raw.isSubscriber : null,
    isClaimed: typeof raw.isClaimed === "boolean" ? raw.isClaimed : null,
    isFacebookActivated:
      typeof raw.isFacebookActivated === "boolean" ? raw.isFacebookActivated : null,
    isIncentivisingUsers:
      typeof raw.isIncentivisingUsers === "boolean" ? raw.isIncentivisingUsers : null,
    facebookPageId: typeof raw.facebookPageId === "string" ? raw.facebookPageId : null,
    facebookPageUrl: typeof raw.facebookPageUrl === "string" ? raw.facebookPageUrl : null,
  };
}

/**
 * Helper — apply `stripCsvInjectionPrefix` only when the value is a
 * non-empty string. Returns null for null/empty inputs (parity with the
 * field's NULL-when-absent contract). Used by Story 35.3 transformer
 * expansion (Task 10).
 */
function applyCsvStrip(value: string | null): string | null {
  if (!value) return null;
  return stripCsvInjectionPrefix(value);
}

/**
 * Transform a single review wire record (public OR merged public+private)
 * into the flat per-review row (`_reportType: "trustpilotReview"`).
 *
 * Applies `stripCsvInjectionPrefix` to every string field destined for
 * export (`title`, `text`, `companyReplyText`, `consumerDisplayName`,
 * `consumerDisplayLocation`, `tags[]`).
 *
 * When `privateApiEntitled === false`, the 8 private-tier fields are
 * stripped (not included on the row); when `true` they are present.
 */
export function transformTrustpilotReview(
  raw: Record<string, unknown>,
  options: { businessUnitId: string; privateApiEntitled: boolean }
): Record<string, unknown> {
  const companyReply = (raw.companyReply ?? null) as
    | Record<string, unknown>
    | null;
  const consumer = (raw.consumer ?? {}) as Record<string, unknown>;
  const stars = integerOrNull(raw.stars) ?? 0;
  const createdAt = stringOrNull(raw.createdAt);
  const replyCreatedAt = companyReply
    ? stringOrNull(companyReply.createdAt)
    : null;
  const replyLatencySeconds =
    createdAt && replyCreatedAt
      ? computeLatencySeconds(createdAt, replyCreatedAt)
      : null;

  const baseRecord: Record<string, unknown> = {
    _reportType: "trustpilotReview" as TrustpilotReportType,
    businessUnitId: options.businessUnitId,
    reviewId: stringOrNull(raw.id),
    date: createdAt,
    stars,
    title: stripCsvInjectionPrefix(safeString(raw.title) ?? ""),
    text: stripCsvInjectionPrefix(safeString(raw.text) ?? ""),
    language: stringOrNull(raw.language),
    createdAt,
    updatedAt: stringOrNull(raw.updatedAt),
    isVerified: typeof raw.isVerified === "boolean" ? raw.isVerified : null,
    numberOfLikes: integerOrNull(raw.numberOfLikes) ?? 0,
    complianceLabels: Array.isArray(raw.complianceLabels)
      ? (raw.complianceLabels as ReadonlyArray<string>)
      : [],
    reviewVerificationLevel: stringOrNull(raw.reviewVerificationLevel),
    consumerId: stringOrNull(consumer.id),
    consumerDisplayName: stripCsvInjectionPrefix(
      safeString(consumer.displayName) ?? ""
    ),
    consumerDisplayLocation: stripCsvInjectionPrefix(
      safeString(consumer.displayLocation) ?? ""
    ),
    consumerNumberOfReviews: integerOrNull(consumer.numberOfReviews) ?? 0,
    companyReplyText: companyReply
      ? stripCsvInjectionPrefix(safeString(companyReply.text) ?? "")
      : null,
    companyReplyCreatedAt: replyCreatedAt,
    companyReplyUpdatedAt: companyReply
      ? stringOrNull(companyReply.updatedAt)
      : null,
    hasReply: companyReply !== null,
    replyLatencySeconds,
    sentimentBucket: trustpilotSentimentBucket(stars),
  };

  // Story 35.3 Batch 1 — derived always-available fields (regardless
  // of `privateApiEntitled`).
  //
  // wordCount per AC #5 — text body word count via `text.trim().split(
  // /\s+/).filter(Boolean).length`. Computed BEFORE CSV-injection strip
  // so the count reflects the original review intent (a leading `=`
  // would otherwise reduce the count by 1).
  const rawText = safeString(raw.text) ?? "";
  const wordCount =
    rawText.trim().length === 0
      ? 0
      : rawText.trim().split(/\s+/).filter(Boolean).length;
  baseRecord.wordCount = wordCount;

  // consumerLifetimeBand per AC #5 — bucketed from
  // `consumer.numberOfReviews`. Surfaces "professional reviewer"
  // detection signal Story 23.3 punted on.
  const consumerNumReviews = integerOrNull(consumer.numberOfReviews) ?? 0;
  baseRecord.consumerLifetimeBand = bucketConsumerLifetime(consumerNumReviews);

  // Private-tier fields — emit only when entitled. Pass `source` and
  // `status` through as raw strings (AC #7 enum-passthrough); the adapter
  // emits forensic logs on unknown values from its caller side.
  if (options.privateApiEntitled) {
    baseRecord.countsTowardsTrustScore =
      typeof raw.countsTowardsTrustScore === "boolean"
        ? raw.countsTowardsTrustScore
        : null;
    baseRecord.status = stringOrNull(raw.status);
    baseRecord.source = stringOrNull(raw.source);
    baseRecord.tags = Array.isArray(raw.tags)
      ? (raw.tags as ReadonlyArray<unknown>)
          .filter((t): t is string => typeof t === "string")
          .map((t) => stripCsvInjectionPrefix(t))
      : [];
    baseRecord.referenceId = stringOrNull(raw.referenceId);
    baseRecord.referralEmail = stringOrNull(raw.referralEmail);
    baseRecord.experiencedAt = stringOrNull(raw.experiencedAt);

    // Story 35.3 Batch 1 — 5 additional paid-tier-gated fields per AC #5.
    baseRecord.countsTowardsLocationTrustScore =
      typeof raw.countsTowardsLocationTrustScore === "boolean"
        ? raw.countsTowardsLocationTrustScore
        : null;
    // `location` — keep as object pass-through. The shape `{ id, name,
    // address: { city, country, ... }, displayLocation }` is per
    // Trustpilot's private business-unit endpoint. PII redaction is
    // handled by `sanitizeTrustpilotPii` for log paths.
    baseRecord.location =
      typeof raw.location === "object" && raw.location !== null
        ? (raw.location as Record<string, unknown>)
        : null;
    // `findReviewer` — `displayName` is PII (existing
    // `consumer.displayName` rule); `profileUrl` is NOT PII (public
    // URL). The PII sanitizer handles the field-level redaction.
    baseRecord.findReviewer =
      typeof raw.findReviewer === "object" && raw.findReviewer !== null
        ? projectFindReviewer(raw.findReviewer as Record<string, unknown>)
        : null;
    // `invitation` — links review to originating invitation send.
    // `templateName` + `sentAt` allowed; `recipientEmail` (if present)
    // redacted by `sanitizeTrustpilotPii`.
    baseRecord.invitation =
      typeof raw.invitation === "object" && raw.invitation !== null
        ? projectInvitation(raw.invitation as Record<string, unknown>)
        : null;
    // `businessUnitHistory` — array of historical BU name objects.
    // BU names are public; CSV-injection-strip applied to `name`.
    baseRecord.businessUnitHistory = Array.isArray(raw.businessUnitHistory)
      ? (raw.businessUnitHistory as ReadonlyArray<unknown>)
          .filter((h): h is Record<string, unknown> => !!h && typeof h === "object")
          .map((h) => ({
            ...h,
            name:
              typeof h.name === "string" ? stripCsvInjectionPrefix(h.name) : null,
          }))
      : [];
  }

  return baseRecord;
}

/**
 * Bucket `consumer.numberOfReviews` into a 4-tier lifetime band per
 * Story 35.3 AC #5.
 *
 * Buckets (lower-bound inclusive):
 *   - `"newcomer"`   = 0..2 reviews
 *   - `"established"` = 3..9 reviews
 *   - `"active"`     = 10..49 reviews
 *   - `"power_reviewer"` = 50+ reviews
 *
 * Note AC #5 says `1-2 → newcomer`, but a missing/zero count rolls into
 * the same bucket as 1-2 (defensive — a brand-new reviewer with 0 prior
 * reviews IS a newcomer). Returns the bucket key as a string.
 */
function bucketConsumerLifetime(
  n: number
): "newcomer" | "established" | "active" | "power_reviewer" {
  if (n >= 50) return "power_reviewer";
  if (n >= 10) return "active";
  if (n >= 3) return "established";
  return "newcomer";
}

/**
 * Project `findReviewer` object — CSV-injection-strip `displayName` +
 * `profileUrl` (Round 3 Red Team defense; URL strings can contain `=`
 * prefix that triggers Excel formula evaluation per Story 35.3 AC #15).
 *
 * Code-review round 1 fix #26 — preserve empty-object semantic. When
 * Trustpilot returns `findReviewer: {}` (a documented "consumer opted
 * out of findReviewer surface" signal), return `{}` rather than
 * collapsing to `{displayName: null, displayLocation: null, profileUrl: null}`
 * — the all-null shape is indistinguishable from a record that explicitly
 * set those fields to null. Empty-object preservation lets downstream
 * widgets check `Object.keys(findReviewer).length === 0` to distinguish
 * "consumer opted out" from "consumer has missing names".
 */
function projectFindReviewer(
  raw: Record<string, unknown>
): Record<string, unknown> {
  // Empty input → empty output (opt-out signal preserved).
  if (Object.keys(raw).length === 0) return {};
  // Otherwise, only project fields that exist on the input — don't
  // inject null defaults for missing keys.
  const out: Record<string, unknown> = { ...raw };
  if (typeof raw.displayName === "string") {
    out.displayName = stripCsvInjectionPrefix(raw.displayName);
  }
  if (typeof raw.displayLocation === "string") {
    out.displayLocation = stripCsvInjectionPrefix(raw.displayLocation);
  }
  if (typeof raw.profileUrl === "string") {
    out.profileUrl = stripCsvInjectionPrefix(raw.profileUrl);
  }
  return out;
}

/**
 * Project `invitation` object — CSV-injection-strip `templateName`
 * (per Story 35.3 AC #15). `recipientEmail` is PII and stripped via
 * `sanitizeTrustpilotPii` on log paths; we still pass it through the
 * record so the production data path has the value when needed.
 *
 * Round 2 fix R2-10 — preserve empty-object semantic in symmetry with
 * `projectFindReviewer`. Trustpilot's documented "no invitation
 * associated with this review" empty-object signal becomes
 * indistinguishable from "Trustpilot returned an invitation record
 * with all-null fields" if we always inject null defaults.
 */
function projectInvitation(
  raw: Record<string, unknown>
): Record<string, unknown> {
  // Empty input → empty output (opt-out / no-invitation signal preserved).
  if (Object.keys(raw).length === 0) return {};
  // Otherwise, only project fields that exist on the input — don't inject
  // null defaults for missing keys.
  const out: Record<string, unknown> = { ...raw };
  if (typeof raw.templateName === "string") {
    out.templateName = stripCsvInjectionPrefix(raw.templateName);
  }
  if (typeof raw.sentAt === "string") {
    out.sentAt = raw.sentAt;
  }
  return out;
}

/**
 * Build the 5-record `reviewDistribution` rollup (always emit all 5 stars
 * even when zero — drives the rating-distribution bar/pie chart). Per
 * Round 5 Edge Case Hunter.
 */
export function buildTrustpilotReviewDistribution(
  reviews: ReadonlyArray<Record<string, unknown>>,
  businessUnitId: string
): Record<string, unknown>[] {
  const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const r of reviews) {
    // Round-then-bound so decimal `stars` in the valid [0.5, 5.5] range
    // (e.g., Trustpilot ever ships 4.5 on a per-review field — only
    // score.stars is integer today, but the wire shape isn't lock-bound)
    // lands in the nearest 1-5 bucket. Pass 2 fix: explicitly accept the
    // [0.5, 5.5] boundary so `Math.round(5.5) === 6` is clamped down to 5
    // instead of silently disappearing. Out-of-range values (negative,
    // > 5.5, etc.) are still dropped as bad data.
    const raw = numberOrNull(r.stars);
    if (raw === null || raw < 0.5 || raw > 5.5) continue;
    const rounded = Math.round(raw);
    const stars = Math.min(5, Math.max(1, rounded));
    counts[stars] = (counts[stars] ?? 0) + 1;
  }
  return [1, 2, 3, 4, 5].map((starRating) => ({
    _reportType: "reviewDistribution" as TrustpilotReportType,
    businessUnitId,
    starRating,
    count: counts[starRating] ?? 0,
  }));
}

/**
 * Build the single `reviewSummary` aggregation row per AC #4.
 *
 * NULL-when-zero contract (Round 5 Edge Case Hunter #1):
 *  - `avgStarsInPeriod` is NULL when `reviews.length === 0`
 *  - `verifiedReviewPercentage` is NULL when `reviews.length === 0`
 *  - `companyReplyRate` is NULL when `reviews.length === 0`
 *  - `avgCompanyReplyLatencySeconds` is NULL when `repliedRecords.length === 0`
 *  - `replyWithin24hPercentage` is NULL when `repliedRecords.length === 0`
 *
 * "0" would render as "instant reply" mis-indicator — NULL forces the
 * widget to show "no data".
 *
 * Story 23.4 — 3 synthetic-aggregate metrics also emitted when a non-null
 * `businessUnitDetails` row is passed (otherwise they're omitted from the
 * row; widgets that bind them then read NULL):
 *  - `reviewVelocityPerMonth` — count of reviews in the last 30 days
 *    (caller responsible for windowing). When called over a 30-day
 *    `reviews` set, == `reviewsInPeriod`.
 *  - `reputationTrendDirection` — 1 / 0 / -1 from `avgStarsInPeriod` vs
 *    `businessUnitDetails.trustScore` (≥ +0.05 → 1, ≤ -0.05 → -1, else 0).
 *  - `trustScoreEligibleReviewPercentage` — `usedForTrustScoreCalculation
 *    / totalReviews * 100`, rounded to 1 decimal place.
 *
 * The build-to-sell reputation-scorecard preset #8 binds
 * `trustScoreEligibleReviewPercentage` as its 4th-slot KPI per ADR-C, so
 * production parity with fixture-mode requires emission here (formerly
 * the values only existed in `demo-accounts.ts` baseMetrics).
 */
export function buildTrustpilotReviewSummary(
  reviews: ReadonlyArray<Record<string, unknown>>,
  businessUnitId: string,
  businessUnitDetails?: Record<string, unknown> | null,
  lifetimeRecords?: ReadonlyArray<Record<string, unknown>> | null
): Record<string, unknown> {
  // `reviewsInPeriod` per AC #4 is the COUNT of reviews in the dateRange
  // (caller pre-filters by date) — includes private-only records lacking
  // stars (e.g., reported/pending reviews).
  const reviewsInPeriod = reviews.length;
  // Pass 2 fix — exclude records lacking `stars` from rate-based
  // aggregations. Private-only reviews surfaced by paid-tier merge may
  // omit stars; counting them as `0` dragged avgStarsInPeriod toward zero
  // and mis-categorized them as negative sentiment.
  const ratedReviews = reviews.filter(
    (r) => integerOrNull(r.stars) !== null
  );
  const ratedTotal = ratedReviews.length;
  const replied = ratedReviews.filter((r) => {
    const reply = r.companyReply as Record<string, unknown> | null | undefined;
    // Pass 2 fix — also exclude empty-object replies (moderation-deleted
    // replies that Trustpilot represents as `{}` instead of removing the
    // field). The `replyLatencySeconds` calculation already handles this
    // defensively, but `replied.length` should match.
    if (reply === null || reply === undefined) return false;
    return Object.keys(reply).length > 0;
  });
  const verifiedCount = ratedReviews.filter((r) => r.isVerified === true).length;
  const starsSum = ratedReviews.reduce((acc, r) => {
    const stars = integerOrNull(r.stars);
    return acc + (stars ?? 0);
  }, 0);

  const replyLatencies = replied
    .map((r) => {
      const reply = r.companyReply as Record<string, unknown>;
      const createdAt = stringOrNull(r.createdAt);
      const replyCreatedAt = stringOrNull(reply.createdAt);
      if (!createdAt || !replyCreatedAt) return null;
      return computeLatencySeconds(createdAt, replyCreatedAt);
    })
    .filter((v): v is number => v !== null);

  const within24h = replyLatencies.filter((s) => s < 86_400).length;

  const avgStarsInPeriod =
    ratedTotal === 0 ? null : Math.round((starsSum / ratedTotal) * 100) / 100;
  const verifiedReviewPercentage =
    ratedTotal === 0
      ? null
      : Math.round((verifiedCount / ratedTotal) * 10000) / 100;
  const companyReplyRate =
    ratedTotal === 0
      ? null
      : Math.round((replied.length / ratedTotal) * 10000) / 100;
  const avgCompanyReplyLatencySeconds =
    replyLatencies.length === 0
      ? null
      : Math.round(
          replyLatencies.reduce((a, b) => a + b, 0) / replyLatencies.length
        );
  const replyWithin24hPercentage =
    replyLatencies.length === 0
      ? null
      : Math.round((within24h / replyLatencies.length) * 10000) / 100;

  // ── Story 23.4 synthetic-aggregate metrics ──────────────────────────
  // Story 23.4 ADR-C — Trustpilot-specific catalogue ADDITION. These
  // three values are computed at aggregation time from `reviews` (the
  // caller-pre-filtered window) and `businessUnitDetails` (the lifetime
  // BU details row). When `businessUnitDetails` is omitted, the values
  // are NULL so widgets binding them render "—" gracefully.
  let reviewVelocityPerMonth: number | null = null;
  let reputationTrendDirection: number | null = null;
  let trustScoreEligibleReviewPercentage: number | null = null;
  if (businessUnitDetails) {
    // Velocity — count over the caller-supplied window. For a 30-day
    // dateRange, == `reviewsInPeriod`.
    reviewVelocityPerMonth = reviewsInPeriod;

    // Story 23.4 — accept BOTH wire-format (`{ score: { trustScore },
    // numberOfReviews: { total, usedForTrustScoreCalculation } }`) AND
    // already-flat (`{ trustScore, totalReviews,
    // reviewsUsedForTrustScoreCalculation }`) shapes, so production
    // (raw wire) and fixture mode (after `transformTrustpilotBusinessUnitDetails`)
    // both feed correctly.
    const score = (businessUnitDetails.score ?? {}) as Record<string, unknown>;
    const numberOfReviews = (businessUnitDetails.numberOfReviews ?? {}) as Record<
      string,
      unknown
    >;
    const trustScore =
      numberOrNull(score.trustScore) ??
      numberOrNull(businessUnitDetails.trustScore);
    const total =
      integerOrNull(numberOfReviews.total) ??
      integerOrNull(businessUnitDetails.totalReviews);
    const eligible =
      integerOrNull(numberOfReviews.usedForTrustScoreCalculation) ??
      integerOrNull(businessUnitDetails.reviewsUsedForTrustScoreCalculation);

    // Trend — avgStarsInPeriod vs lifetime trustScore. ≥ +0.05 → 1
    // (improving), ≤ -0.05 → -1 (declining), else 0 (flat). The 0.05
    // dead band prevents noisy flapping on near-flat trends.
    if (avgStarsInPeriod !== null && trustScore !== null) {
      const delta = avgStarsInPeriod - trustScore;
      reputationTrendDirection = delta >= 0.05 ? 1 : delta <= -0.05 ? -1 : 0;
    }

    // Eligibility ratio — usedForTrustScoreCalculation / totalReviews * 100.
    if (total !== null && total > 0 && eligible !== null) {
      trustScoreEligibleReviewPercentage =
        Math.round((eligible / total) * 1000) / 10;
    }
  }

  // Story 23.8 — `unrepliedReviewCount` derived sub-aggregate. The 23.8
  // Awaiting Response Queue widget consumes this per-platform on both
  // Google Reviews + Trustpilot. Computed as ratedTotal - replied.length
  // — every rated review that does NOT have a companyReply counts as
  // unreplied (mirrors Google's `unrepliedReviewCount = noReplyCount`
  // semantic at google-reviews.ts:1392; Trustpilot has no PENDING state,
  // so the bookkeeping is simpler — replied vs unreplied is binary).
  //
  // Story 23.8 Pass 2 Code Review fix — EH-P2-03 + EH-P2-04:
  //   - NULL when ratedTotal === 0 (parallel to companyReplyRate's
  //     zero-state guard). Without this, a newly-listed BU with 0
  //     reviews would render "0 awaiting" + green "All caught up"
  //     styling, a false-success-affirmation.
  //   - Math.max(0, ...) defense against a future refactor where
  //     `replied` could be sourced from a wider list than `ratedReviews`
  //     (negative count would crash the renderer).
  const unrepliedReviewCount =
    ratedTotal === 0 ? null : Math.max(0, ratedTotal - replied.length);

  // ── Story 35.3 Batch 3 — lifetime aggregates over paginated history ─
  // When the caller passes `lifetimeRecords` (full paginated set), we
  // compute 4 lifetime aggregates per AC #7. When omitted (= Story 23.3
  // baseline default-page-1-only call site), all 4 are NULL — widgets
  // bind them and render "—" gracefully.
  //
  // Code-review round 1 fix #10 + #25 — when EVERY lifetime record lacks
  // a valid `createdAt` (or `stars`), maintain consistency by NULL-ing
  // ALL 4 lifetime fields rather than emitting `count=N, oldest=null`.
  // A future widget showing "150 lifetime reviews, oldest: —" would be
  // logically contradictory.
  let lifetimeReviewCount: number | null = null;
  let oldestReviewDate: string | null = null;
  let lifetimeAvgStars: number | null = null;
  let lifetimeReplyRate: number | null = null;
  if (lifetimeRecords && lifetimeRecords.length > 0) {
    // Round 2 fix R2-9 — count only records with valid `createdAt` so
    // lifetimeReviewCount is consistent with `oldestReviewDate` (avoid
    // the "150 reviews, oldest: —" inconsistency). A record missing
    // `createdAt` cannot contribute to time-series aggregates and
    // shouldn't inflate the headline count.
    let minMs = Number.POSITIVE_INFINITY;
    let withValidCreatedAt = 0;
    let starsSumLifetime = 0;
    let ratedLifetime = 0;
    let repliedLifetimeRated = 0;
    let bestOldestStr: string | null = null;
    for (const r of lifetimeRecords) {
      const createdAtStr = typeof r.createdAt === "string" ? r.createdAt : null;
      if (!createdAtStr) continue;
      const ms = Date.parse(createdAtStr);
      if (!Number.isFinite(ms)) continue;
      withValidCreatedAt++;
      if (ms < minMs) {
        minMs = ms;
        bestOldestStr = createdAtStr;
      }
      const lifeStars = integerOrNull(r.stars);
      if (lifeStars !== null) {
        starsSumLifetime += lifeStars;
        ratedLifetime++;
        // Round 2 fix R2-8 — lifetimeReplyRate denominator is `ratedLifetime`
        // (matches in-period `companyReplyRate` semantic at line 471 above),
        // NOT `lifetimeReviewCount`. The two metrics should produce
        // identical rates over identical data sets.
        const reply = r.companyReply as Record<string, unknown> | null | undefined;
        if (reply !== null && reply !== undefined && Object.keys(reply).length > 0) {
          repliedLifetimeRated++;
        }
      }
    }

    if (withValidCreatedAt === 0) {
      // No valid timestamps anywhere → NULL ALL 4 to preserve widget
      // consistency.
    } else {
      lifetimeReviewCount = withValidCreatedAt;
      oldestReviewDate = bestOldestStr;
      lifetimeAvgStars =
        ratedLifetime === 0
          ? null
          : Math.round((starsSumLifetime / ratedLifetime) * 100) / 100;
      lifetimeReplyRate =
        ratedLifetime === 0
          ? null
          : Math.round((repliedLifetimeRated / ratedLifetime) * 10000) / 100;
    }
  }

  return {
    _reportType: "reviewSummary" as TrustpilotReportType,
    businessUnitId,
    reviewsInPeriod,
    avgStarsInPeriod,
    verifiedReviewPercentage,
    companyReplyCount: replied.length,
    companyReplyRate,
    avgCompanyReplyLatencySeconds,
    replyWithin24hPercentage,
    // Story 23.8 — Awaiting Response Queue widget source.
    unrepliedReviewCount,
    reviewVelocityPerMonth,
    reputationTrendDirection,
    trustScoreEligibleReviewPercentage,
    // Story 35.3 Batch 3 — lifetime aggregates over paginated history.
    // NULL when caller did NOT opt into pagination (preserves Story 23.3
    // baseline; widgets binding these render "—" gracefully).
    lifetimeReviewCount,
    oldestReviewDate,
    lifetimeAvgStars,
    lifetimeReplyRate,
  };
}

// ============================================================================
// Story 35.3 Batch 2 — Invitations API transformer (`invitationData` discriminator)
// ============================================================================

/**
 * Transform a Trustpilot Invitations API response (flat wire object)
 * into the single-row `_reportType: "invitationData"` record.
 *
 * Story 35.3 AC #6:
 *   - 8 wire counts (`total_invitations_*` + `templates_active` derived
 *     from `templates[].isActive` count + `invitation_links_active`
 *     derived from `invitation_links[].isActive` count).
 *   - 3 synthetic rates (open / conversion / bounce — NULL when sent = 0).
 *   - Defaults missing keys to 0 (counts) or null (rates) per AC #21
 *     Round 5 Edge Case Hunter — Trustpilot occasionally returns sparse
 *     responses.
 *   - On full shape mismatch the caller (`fetchTrustpilotInvitationData`)
 *     already returned `invalid_response`; we don't get a chance to be
 *     called. This transformer is only invoked on a documented (or
 *     partially-documented) response.
 */
export function transformTrustpilotInvitationData(
  raw: Record<string, unknown>,
  businessUnitId: string
): Record<string, unknown> {
  // Code-review round 1 fix #19 — clamp negative wire counts to 0
  // (counts are physically non-negative). A pathological/buggy response
  // with `total_invitations_sent: -50` should not flow downstream.
  // Code-review round 1 fix #20 — distinguish null literal from missing
  // key. If the wire emits `total_invitations_sent: null` explicitly,
  // surface as null (caller signal "no data") rather than coerce to 0.
  const invitationsSent = countOrNull(raw, "total_invitations_sent");
  const invitationsOpened = countOrNull(raw, "total_invitations_opened");
  const invitationsClicked = countOrNull(raw, "total_invitations_clicked");
  const invitationsBounced = countOrNull(raw, "total_invitations_bounced");
  const invitationsUnsubscribed = countOrNull(raw, "total_invitations_unsubscribed");
  const invitationsConverted = countOrNull(raw, "total_invitations_converted");

  // templates_active — count of `templates[].isActive === true` OR truthy
  // non-strict (Story 35.3 fix #27 — forward-compat for shape drift where
  // Trustpilot may emit `isActive: 1` or `"true"`).
  // Round 2 fix R2-21 — distinguish "templates key missing" (null —
  // matches the round 1 fix #20 null-vs-missing convention on the wire
  // counts) from "templates: []" (0 — explicit empty array).
  const templatesActive = !Object.prototype.hasOwnProperty.call(raw, "templates")
    ? null
    : Array.isArray(raw.templates)
      ? (raw.templates as ReadonlyArray<unknown>).filter((t) =>
          isTemplateActive(t)
        ).length
      : 0;

  const invitationLinksActive = !Object.prototype.hasOwnProperty.call(
    raw,
    "invitation_links"
  )
    ? null
    : Array.isArray(raw.invitation_links)
      ? (raw.invitation_links as ReadonlyArray<unknown>).filter((l) =>
          isTemplateActive(l)
        ).length
      : 0;

  // Derived rates — NULL when sent === null OR sent === 0 (preserves
  // "no data yet" signal vs literal 0 which would render as "0% open
  // rate" — a false-success affirmation per the AC #21 empty-set
  // semantics). Code-review round 1 fix #12 — clamp to [0, 100] so
  // upstream data inconsistencies (e.g., opened > sent due to multi-
  // open tracking) cannot produce > 100% rates that break gauge widgets.
  const invitationOpenRate = computeRate(invitationsOpened, invitationsSent);
  const invitationConversionRate = computeRate(
    invitationsConverted,
    invitationsSent
  );
  const invitationBounceRate = computeRate(invitationsBounced, invitationsSent);

  return {
    _reportType: "invitationData" as TrustpilotReportType,
    businessUnitId,
    invitationsSent,
    invitationsOpened,
    invitationsClicked,
    invitationsBounced,
    invitationsUnsubscribed,
    invitationsConverted,
    templatesActive,
    invitationLinksActive,
    invitationOpenRate,
    invitationConversionRate,
    invitationBounceRate,
  };
}

/**
 * Project a wire-format count field with explicit null-vs-missing
 * distinction + non-negative clamp. Story 35.3 code-review round 1
 * fixes #19 + #20.
 *
 * Returns:
 *   - `null` when the key is MISSING from the wire payload (caller bug)
 *     OR when the key is present with literal `null` value
 *   - `0` when the wire value is negative (clamp; counts are physically
 *     non-negative — surface a forensic log if needed at caller)
 *   - the integer value otherwise (parsed via existing `integerOrNull`
 *     which also handles string-as-integer like `"50"`)
 */
function countOrNull(
  raw: Record<string, unknown>,
  key: string
): number | null {
  if (!Object.prototype.hasOwnProperty.call(raw, key)) return null;
  const value = raw[key];
  if (value === null) return null;
  const parsed = integerOrNull(value);
  if (parsed === null) return null;
  return Math.max(0, parsed);
}

/**
 * Compute a rate percentage. Story 35.3 code-review round 1 fix #12 +
 * #20. NULL when sent is null/0; clamped to [0, 100].
 */
function computeRate(numerator: number | null, denominator: number | null): number | null {
  if (denominator === null || denominator === 0) return null;
  if (numerator === null) return null;
  const raw = (numerator / denominator) * 100;
  // Clamp to [0, 100]. A clamp at 100 surfaces "perfect rate" — agencies
  // can still cross-check via the raw count fields.
  const clamped = Math.max(0, Math.min(100, raw));
  return Math.round(clamped * 100) / 100;
}

/**
 * Truthy-coerce `isActive` for templates/links. Story 35.3 fix #27 —
 * Trustpilot ships shape drift where `isActive` may be `true`, `1`,
 * `"true"`, etc. Reject only obvious falsy values.
 */
function isTemplateActive(item: unknown): item is Record<string, unknown> {
  if (!item || typeof item !== "object") return false;
  const v = (item as Record<string, unknown>).isActive;
  if (v === true) return true;
  if (v === 1) return true;
  if (typeof v === "string" && v.toLowerCase() === "true") return true;
  return false;
}

// ============================================================================
// Tiny value-coercion helpers
// ============================================================================

function safeString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function numberOrNull(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function integerOrNull(value: unknown): number | null {
  const n = numberOrNull(value);
  if (n === null) return null;
  return Math.trunc(n);
}

function computeLatencySeconds(
  createdAt: string,
  replyCreatedAt: string
): number | null {
  const c = Date.parse(createdAt);
  const r = Date.parse(replyCreatedAt);
  if (!Number.isFinite(c) || !Number.isFinite(r)) return null;
  if (r < c) return null; // Defensive: reply before review = bad data
  return Math.round((r - c) / 1000);
}
